import { motion } from "framer-motion";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import heroImage from "@/assets/ibiza-lifestyle-hero.webp";
import imgChauffeur from "@/assets/lifestyle-chauffeur.webp";
import imgDining from "@/assets/lifestyle-dining-ibiza.webp";
import imgTransport from "@/assets/lifestyle-transport.webp";
import imgWellness from "@/assets/lifestyle-wellness.webp";
import imgRestaurant from "@/assets/lifestyle-restaurant.webp";
import imgClub from "@/assets/lifestyle-nightlife-ibiza.webp";
import imgChef from "@/assets/lifestyle-chef.webp";
import imgSecurity from "@/assets/lifestyle-security.webp";
import imgSpecial from "@/assets/lifestyle-special.webp";

const ease = [0.22, 1, 0.36, 1] as const;

const services = [
  {
    title: "Mobility & Transport",
    description:
      "Private chauffeur services, luxury vehicles, and VIP transport arranged seamlessly throughout your stay. From airport arrivals to daily movement, every journey is aligned with your schedule.",
    image: imgChauffeur,
    price: "From €75/h (chauffeur) · €350/day (transport)",
  },
  {
    title: "Dining & Reservations",
    description:
      "Tables secured at Ibiza's most sought-after venues, including Lío, Cipriani, Zuma, Casa Jondal, and Nobu Ibiza — arranged with care and attention to detail.",
    image: imgDining,
    price: "From €50+",
  },
  {
    title: "Nightlife & Access",
    description:
      "Priority access to Ibiza's most iconic venues, including Hï Ibiza, Ushuaïa, Pacha, Lío, and Club Chinois. From guest list entry to VIP tables, every detail is arranged in advance for a seamless and elevated night.",
    image: imgClub,
    price: "From €250 pp",
  },
  {
    title: "Wellness & Personal Care",
    description:
      "In-villa massages, beauty treatments, and private wellness sessions, designed to create moments of calm within your own space.",
    image: imgWellness,
    price: "From €150",
  },
  {
    title: "Private Dining & In-Residence Services",
    description:
      "Personal chefs and tailored dining experiences within your residence, offering privacy, comfort, and a more refined way to dine.",
    image: imgChef,
    price: "From €120 pp",
  },
  {
    title: "Security & Personal Assistance",
    description:
      "Discreet services ensuring privacy, comfort, and peace of mind throughout your stay.",
    image: imgSecurity,
    price: "From €40/h",
    objectPosition: "center 20%",
  },
  {
    title: "Bespoke Requests",
    description:
      "From last-minute arrangements to highly specific experiences, every request is handled with precision and care.",
    image: imgSpecial,
    objectPosition: "center 25%",
    imageScale: "scale-110",
  },
];

const IbizaLifestyle = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />

      {/* Hero */}
      <section className="relative h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Arzani Lifestyle Ibiza" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/20 to-foreground/10" />
        </div>
        <div className="relative z-10 px-8 md:px-16 lg:px-20 pb-20 md:pb-28 max-w-2xl">
          <motion.span
            className="font-label text-primary-foreground/40 tracking-[0.3em] text-[9px] block mb-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 2, ease }}
          >
            Personal Services · Ibiza
          </motion.span>
          <motion.h1
            className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground leading-[1] tracking-wide uppercase mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 2, ease }}
          >
            Arzani Lifestyle
          </motion.h1>
          <motion.p
            className="font-body text-primary-foreground/50 text-sm leading-[1.9] max-w-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 2, ease }}
          >
            Lifestyle is not defined by what you have access to, but by how effortlessly it comes together.
          </motion.p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-32 md:py-44 lg:py-56">
        <div className="mx-auto px-8 md:px-16 max-w-3xl">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.6, ease }}
          >
            <span className="font-label text-accent block mb-10 tracking-[0.3em] text-[9px]">
              The Experience
            </span>
            <p className="font-body text-muted-foreground text-sm md:text-base leading-[2] max-w-2xl mx-auto mb-8">
              Through Arzani Lifestyle, every detail of your Ibiza experience is thoughtfully arranged — creating a seamless sense of ease from the moment you arrive. From private transport and wellness to dining and exclusive access, each element is handled with precision and care.
            </p>
            <p className="font-body text-muted-foreground text-sm md:text-base leading-[2] max-w-2xl mx-auto">
              Our approach is discreet and highly personal, allowing you to move through the island without interruption, with everything already in place.
            </p>
            <div className="divider-subtle mx-auto mt-14" />
          </motion.div>
        </div>
      </section>

      {/* Services — alternating layout */}
      <section className="pt-32 md:pt-44 pb-16 md:pb-20">
        <div className="mx-auto px-8 md:px-16 max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.6, ease }}
          >
            <span className="font-label text-accent/60 block mb-8 tracking-[0.3em] text-[9px]">
              Services
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground tracking-wide uppercase leading-[1.1]">
              What We Offer
            </h2>
          </motion.div>
        </div>
      </section>

      {services.map((service, index) => {
        const isEven = index % 2 === 0;
        return (
          <section key={service.title} className="mb-0">
            <motion.div
              className="grid grid-cols-1 lg:grid-cols-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.6, ease }}
            >
              <div className={`lg:col-span-7 relative overflow-hidden ${!isEven ? "lg:order-2" : ""}`}>
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-[50vh] lg:h-[65vh] object-cover"
                  style={service.objectPosition ? { objectPosition: service.objectPosition } : undefined}
                  loading="lazy"
                />
              </div>
              <div className={`lg:col-span-5 flex items-center ${!isEven ? "lg:order-1" : ""}`}>
                <div className={`px-8 md:px-16 lg:px-16 xl:px-20 py-20 lg:py-0 ${!isEven ? "lg:pl-20 xl:pl-24" : "lg:pr-20 xl:pr-24"}`}>
                  <span className="font-label text-accent/60 block mb-6 tracking-[0.3em] text-[8px]">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-2xl md:text-3xl text-foreground mb-6 tracking-wide uppercase leading-[1.15]">
                    {service.title}
                  </h3>
                  <div className="w-8 h-px bg-foreground/10 mb-8" />
                  <p className="font-body text-muted-foreground text-sm leading-[2] max-w-sm mb-8">
                    {service.description}
                  </p>
                  {"price" in service && service.price && (
                    <p className="font-label text-accent text-[10px] tracking-[0.15em] mb-8">
                      {service.price}
                    </p>
                  )}
                  <a
                    href="https://wa.me/message/2XEKJ5STR2IZJ1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-8 py-3 border border-foreground/20 text-foreground font-label tracking-[0.2em] text-[10px] hover:bg-foreground hover:text-background transition-all duration-500"
                  >
                    Choose
                  </a>
                </div>
              </div>
            </motion.div>
          </section>
        );
      })}

      {/* The Arzani Approach */}
      <section className="py-36 md:py-48 lg:py-56">
        <div className="mx-auto px-8 md:px-16 max-w-2xl">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.6, ease }}
          >
            <span className="font-label text-accent/60 block mb-10 tracking-[0.3em] text-[9px]">
              Our Approach
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground mb-10 tracking-wide uppercase leading-[1.1]">
              The Arzani Approach
            </h2>
            <div className="divider-subtle mx-auto mb-12" />
            <p className="font-body text-muted-foreground text-sm md:text-base leading-[2]">
              Every arrangement is handled quietly in the background, allowing you to focus entirely on your time in Ibiza and your surroundings. Our approach is discreet and highly personal — allowing you to move through the island effortlessly, with everything already in place.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Statement */}
      <section className="py-32 md:py-44 lg:py-52">
        <div className="mx-auto px-8 max-w-3xl text-center">
          <motion.p
            className="font-display text-3xl md:text-5xl lg:text-6xl text-foreground leading-[1.1] tracking-wide"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.6, ease }}
          >
            Everything is arranged<br />in advance.
          </motion.p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-36 md:py-48 bg-warm-black">
        <div className="mx-auto px-8 md:px-16 max-w-xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.6, ease }}
          >
            <span className="font-label text-bronze/60 block mb-10 tracking-[0.3em] text-[9px]">
              Contact
            </span>
            <h2 className="font-display text-3xl md:text-4xl text-primary-foreground mb-6 leading-[1.15] tracking-wide">
              Have a specific request?
            </h2>
            <p className="font-body text-primary-foreground/40 text-sm leading-[2] mb-14">
              Our team is available to assist.
            </p>
            <a
              href="https://wa.me/message/2XEKJ5STR2IZJ1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-4 border border-primary-foreground/20 text-primary-foreground/70 font-label tracking-[0.25em] text-[10px] hover:bg-primary-foreground/5 hover:border-primary-foreground/30 transition-all duration-700"
            >
              Contact via WhatsApp
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IbizaLifestyle;
